// Catalog Shell - Progress Bar Component
// ============================================================================

import { createElement } from '../../utils/dom';

// ============================================================================
// Types
// ============================================================================

export type ProgressVariant = 'default' | 'success' | 'warning' | 'error';

export interface ProgressBarConfig {
    /** Initial value (0-100). Default: 0 */
    value?: number;
    /** Display text below the bar. Default: none */
    label?: string;
    /** Visual variant. Default: 'default' */
    variant?: ProgressVariant;
    /** If true, shows an indeterminate animation. Default: false */
    indeterminate?: boolean;
    /** If true, shows percentage text inside the bar. Default: false */
    showPercentage?: boolean;
}

export interface ProgressBar {
    /** The root DOM element */
    element: HTMLElement;
    /** Update the progress value (0-100) */
    setValue(value: number): void;
    /** Update the label text */
    setLabel(label: string): void;
    /** Switch between determinate and indeterminate */
    setIndeterminate(indeterminate: boolean): void;
    /** Update the variant */
    setVariant(variant: ProgressVariant): void;
}

// ============================================================================
// Factory
// ============================================================================

export function createProgressBar(config: ProgressBarConfig = {}): ProgressBar {
    const {
        value = 0,
        label,
        variant = 'default',
        indeterminate = false,
        showPercentage = false,
    } = config;

    let currentVariant = variant;

    // Build DOM
    const container = createElement('div', {
        className: `cs-progress cs-progress--${variant}`,
    });

    const bar = createElement('div', { className: 'cs-progress-bar' });

    const fill = createElement('div', {
        className: 'cs-progress-bar-fill',
        style: indeterminate ? '' : `width: ${clamp(value)}%`,
    });

    let percentText: HTMLElement | null = null;
    if (showPercentage) {
        percentText = createElement('span', {
            className: 'cs-progress-bar-text',
            textContent: `${Math.round(clamp(value))}%`,
        });
        fill.appendChild(percentText);
    }

    bar.appendChild(fill);
    container.appendChild(bar);

    if (indeterminate) {
        container.classList.add('cs-progress--indeterminate');
    }

    let labelEl: HTMLElement | null = null;
    if (label !== undefined) {
        labelEl = createElement('div', {
            className: 'cs-progress-label',
            textContent: label,
        });
        container.appendChild(labelEl);
    }

    // API
    return {
        element: container,

        setValue(v: number) {
            const clamped = clamp(v);
            fill.style.width = `${clamped}%`;
            if (percentText) {
                percentText.textContent = `${Math.round(clamped)}%`;
            }
        },

        setLabel(text: string) {
            if (!labelEl) {
                labelEl = createElement('div', {
                    className: 'cs-progress-label',
                    textContent: text,
                });
                container.appendChild(labelEl);
            } else {
                labelEl.textContent = text;
            }
        },

        setIndeterminate(ind: boolean) {
            if (ind) {
                container.classList.add('cs-progress--indeterminate');
            } else {
                container.classList.remove('cs-progress--indeterminate');
            }
        },

        setVariant(v: ProgressVariant) {
            container.classList.remove(`cs-progress--${currentVariant}`);
            container.classList.add(`cs-progress--${v}`);
            currentVariant = v;
        },
    };
}

function clamp(value: number): number {
    return Math.max(0, Math.min(100, value));
}
